import { S } from '../core/game';
import { clamp100, dayId, XiaoyuPhase } from '../core/state';
import { bus } from '../core/bus';
import { m } from '../i18n';
import { zoneName } from '../i18n/labels';
import { sfx } from '../audio/sfx';
import { Effects } from '../render/effects';
import { skyTint } from '../render/lighting';
import { wind } from '../render/nature';
import { Actor } from './actor';
import { Ambient } from './ambient';
import { input } from './input';
import { MAPS, areaAt, doorAt, objectsOn, rectContains, rectDistance, mapSize, type Door, type MapDef, type MapId, type Rect, type WorldObject } from './map';
import { riding, RIDE_SPEED } from '../content/places';
import { CAR_LANES } from './maps/town';
import { spotHere, type Spot } from '../content/treasure';

/** The fenced riding paddock in town (inner area, px). */
const PADDOCK: Rect = { x: 59 * 16 + 4, y: 50 * 16 + 4, w: 28 * 16 - 8, h: 7 * 16 - 6 };
import { Navigator } from './movement';
import { NPCS, XIAOYU_IN, XIAOYU_OUT, type NpcDef, type NpcId } from './npcs';
import type { Facing } from '../render/sprites';

/** Game minutes that pass per real second (a full day ≈ 15 real minutes). */
export const MINUTES_PER_SECOND = 0.85;
const SPEED = 76;
const INTERACT_OBJ = 20;
const INTERACT_NPC = 24;
const HOP_TIME = 0.42;
const FACING_DIR: Record<Facing, { x: number; y: number }> = { up: { x: 0, y: -1 }, down: { x: 0, y: 1 }, left: { x: -1, y: 0 }, right: { x: 1, y: 0 } };
const HOP_HEIGHT = 11;
/** Knee-high things a jump clears (hedges and fences are handled as tiles). */
const LOW_OBJECTS = new Set<string>(['bush1', 'bush2', 'bush3', 'mailbox']);
const LOW_DECOR = new Set<string>(['bushDeco', 'flowerbed', 'hay', 'hydrant', 'trashBin', 'trough', 'toyBox', 'bikeRack']);
/** Let the camera overshoot so the HUD and touch controls never cover the map edges. */
const PAD_TOP = 104;
const PAD_BOTTOM = 80;
const FADE = 0.22;

export type Target = { kind: 'object'; obj: WorldObject } | { kind: 'npc'; npc: NpcDef } | { kind: 'spot'; spot: Spot };

export interface WorldHooks {
  interact(t: Target): void;
  /** Rules that run every frame the world is live (needs, quests, calls...). */
  tick(dt: number): void;
  mapChanged?(map: MapId): void;
  /** Veto a door (closed school, etc.). Return false to stay put. */
  canUse?(door: Door): boolean;
}

interface NpcRuntime {
  actor: Actor;
  home: { x: number; y: number };
  goal: { x: number; y: number } | null;
  wait: number;
  /** Walking route when moving to a new spot or towards a door. */
  path: { x: number; y: number }[];
  /** Heading out of this map; removed once the door is reached. */
  leaving: boolean;
}

/** A bus trip along the main street: it pulls in, you get on, it drives, you get off. */
export interface BusRide {
  phase: 'arrive' | 'board' | 'drive' | 'alight' | 'leave';
  t: number;
  x: number;
  y: number;
  dir: 1 | -1;
  /** The traffic lane it drives in (cars queue behind it). */
  lane: number;
  from: { x: number; y: number };
  to: { x: number; y: number };
  onArrive: () => void;
}

const BUS_ARRIVE = 2.4;
const BUS_SPEED = 170;

export class World {
  map: MapDef = MAPS.school;
  nav!: Navigator;
  readonly player = new Actor(120, 80);
  readonly pet = { x: 136, y: 84, hop: 0 };
  readonly kitten = { x: 150, y: 86, actor: new Actor(150, 86) };
  readonly npcs = new Map<NpcId, NpcRuntime>();
  readonly fx = new Effects();
  readonly ambient = new Ambient();
  near: Target | null = null;
  camX = 0;
  camY = 0;
  viewW = 360;
  viewH = 640;
  tapMark: { x: number; y: number; age: number } | null = null;
  time = 0;
  /** Screen fade for map transitions: 0 = clear, 1 = black. */
  fade = 0;

  private path: { x: number; y: number }[] = [];
  private pending: Target | null = null;
  private stuckFor = 0;
  private navKey = '';
  private doorLock = true;
  private warping: { door: Door; t: number; swapped: boolean } | null = null;
  private familyTimer = 0;
  /** A hop in progress: from → to over HOP_TIME seconds. */
  private hop: { t: number; fx: number; fy: number; tx: number; ty: number } | null = null;
  /** Current jump height (px) for the renderer. */
  hopZ = 0;
  /** The bus you're waiting for / riding (town only). */
  ride: BusRide | null = null;
  private paddockWarned = false;
  /** NPCs already placed on this map (new arrivals walk in through the door). */
  private npcInit = false;
  private emit = { steam: 0, motes: 0, petals: 0, flies: 0, notes: 0, leaves: 0 };

  constructor(private hooks: WorldHooks) {
    this.ensureNav();
    bus.on('float', ({ text, color }) => this.fx.float(text, color, this.player.x, this.player.y - 30));
    bus.on('burst', ({ kind, count }) => {
      const map = { sparkle: 'spark', heart: 'heart', note: 'note', drop: 'drop' } as const;
      this.fx.burst(map[kind], this.player.x, this.player.y - 22, count ?? 8);
    });
  }

  /** Rebuild collision when the map or the day (day-only objects) changes. */
  private ensureNav(): void {
    const day = dayId(S());
    const key = `${this.map.id}:${day}`;
    if (key === this.navKey) return;
    const rects: Rect[] = [];
    const low: Rect[] = [];
    for (const o of objectsOn(this.map.id, day)) if (o.solid) {
      rects.push(o.rect);
      if (LOW_OBJECTS.has(o.id)) low.push(o.rect);
    }
    for (const d of this.map.decor) if (d.solid) {
      rects.push(d.solid);
      if (LOW_DECOR.has(d.kind)) low.push(d.solid);
    }
    this.nav = new Navigator(this.map, rects, low);
    this.navKey = key;
  }

  /** Put the player on a map (new day, collapse, load...) and snap the camera. */
  teleport(map: MapId, x: number, y: number, facing: Facing = 'down'): void {
    const changed = map !== this.map.id;
    this.map = MAPS[map];
    this.ensureNav();
    this.player.x = x;
    this.player.y = y;
    this.player.facing = facing;
    this.pet.x = x + 14;
    this.pet.y = y + 2;
    this.kitten.x = x - 14;
    this.kitten.y = y + 2;
    this.path = [];
    this.pending = null;
    this.doorLock = true;
    this.npcs.clear();
    this.npcInit = false;
    this.fx.clear();
    const s = S();
    s.player.map = map;
    s.player.x = x;
    s.player.y = y;
    this.snapCamera();
    if (changed) this.hooks.mapChanged?.(map);
    bus.emit('change');
  }

  snapCamera(): void {
    const c = this.cameraTarget();
    this.camX = c.x;
    this.camY = c.y;
  }

  areaKey(): string {
    return areaAt(this.map, this.player.x, this.player.y);
  }

  area(): string {
    return zoneName(areaAt(this.map, this.player.x, this.player.y));
  }

  /** Tap/click in world coordinates: walk there (A*), and interact if a target was tapped. */
  tapAt(wx: number, wy: number): void {
    if (this.warping || this.onBus) return;
    const target = this.targetAt(wx, wy);
    let gx = wx, gy = wy;
    if (target) {
      const r = targetRect(target, S());
      if (this.distanceTo(target) < (target.kind === 'npc' ? INTERACT_NPC : INTERACT_OBJ)) {
        this.player.face(r.x + r.w / 2, r.y + r.h / 2);
        this.hooks.interact(target);
        return;
      }
      // Aim for the spot in front of the target (below it), the natural place to stand.
      gx = r.x + r.w / 2;
      gy = r.y + r.h + 8;
    }
    const p = this.nav.findPath(this.player.x, this.player.y, gx, gy);
    this.pending = target;
    this.path = p ?? [];
    this.stuckFor = 0;
    this.tapMark = { x: wx, y: wy, age: 0 };
  }

  /** Drag-to-steer: keep walking towards the finger without interacting. */
  steer(wx: number, wy: number): void {
    this.pending = null;
    this.path = this.nav.findPath(this.player.x, this.player.y, wx, wy) ?? [];
  }

  interact(): void {
    if (this.near && !this.onBus) this.hooks.interact(this.near);
  }

  /** Fade out and reappear somewhere else (train, MRT): like a door, without a door. */
  travel(to: MapId, arrive: { x: number; y: number; facing: Facing }): void {
    if (this.warping) return;
    this.cancelMove();
    this.warping = { door: { tiles: [], to, arrive }, t: 0, swapped: false };
  }

  /** True from the moment you wait for the bus until you've stepped off. */
  get onBus(): boolean {
    return !!this.ride && this.ride.phase !== 'leave';
  }

  /** Inside the bus (the player sprite is hidden). */
  get inBus(): boolean {
    return !!this.ride && (this.ride.phase === 'drive' || (this.ride.phase === 'board' && this.ride.t > 0.25) || (this.ride.phase === 'alight' && this.ride.t < 0.25));
  }

  /** Call the bus at `from` (where you stand) and ride it to `to`. */
  takeBus(from: { x: number; y: number }, to: { x: number; y: number }, onArrive: () => void): void {
    if (this.map.id !== 'town' || this.ride) return;
    const dir = to.x > from.x ? 1 : -1;
    const lane = dir > 0 ? CAR_LANES[0].y : CAR_LANES[1].y;
    // Keep the roof clear of the kerb so you're never hidden while you wait.
    const y = Math.max(lane, from.y + 30);
    this.cancelMove();
    this.player.x = from.x;
    this.player.y = from.y;
    this.player.face(from.x, y);
    this.ride = { phase: 'arrive', t: 0, x: from.x - dir * 260, y, dir, lane, from, to, onArrive };
  }

  private updateBus(dt: number): void {
    const r = this.ride!;
    r.t += dt;
    const ease = (k: number) => 1 - (1 - k) ** 3;
    switch (r.phase) {
      case 'arrive':
        r.x = r.from.x - r.dir * 260 * (1 - ease(Math.min(1, r.t / BUS_ARRIVE)));
        if (r.t >= BUS_ARRIVE) {
          r.phase = 'board';
          r.t = 0;
          sfx('door');
        }
        break;
      case 'board':
        if (r.t >= 0.6) {
          r.phase = 'drive';
          r.t = 0;
        }
        break;
      case 'drive': {
        const dist = Math.abs(r.to.x - r.from.x);
        const dur = dist / BUS_SPEED + 1;
        const k = Math.min(1, r.t / dur);
        // Smooth start and stop.
        r.x = r.from.x + (r.to.x - r.from.x) * (k * k * (3 - 2 * k));
        this.player.x = r.x;
        this.player.y = r.to.y;
        if (k >= 1) {
          r.phase = 'alight';
          r.t = 0;
          this.player.x = r.to.x;
          sfx('door');
        }
        break;
      }
      case 'alight':
        if (r.t >= 0.5) {
          r.phase = 'leave';
          r.t = 0;
          this.player.face(r.to.x, r.to.y + 20);
          r.onArrive();
        }
        break;
      case 'leave':
        r.x += r.dir * Math.min(150, 20 + r.t * 90) * dt;
        if (r.t > 4) this.ride = null;
        break;
    }
  }

  /** Hop forwards (or in place): clears hedges, fences, bushes and other low things. */
  jump(): void {
    if (this.hop || this.warping || this.onBus || riding.on || this.player.down) return;
    const a = input.axis();
    const dir = a.x || a.y ? a : FACING_DIR[this.player.facing];
    const land = this.nav.hopLanding(this.player.x, this.player.y, dir.x, dir.y) ?? { x: this.player.x, y: this.player.y };
    this.cancelMove();
    this.hop = { t: 0, fx: this.player.x, fy: this.player.y, tx: land.x, ty: land.y };
    sfx('hop');
  }

  get jumping(): boolean {
    return !!this.hop;
  }

  cancelMove(): void {
    this.path = [];
    this.pending = null;
  }

  /** Remaining waypoints (for the path preview). */
  route(): readonly { x: number; y: number }[] {
    return this.path;
  }

  private targetAt(wx: number, wy: number): Target | null {
    const s = S();
    for (const npc of NPCS) {
      const rt = this.npcs.get(npc.id);
      if (rt && Math.abs(wx - rt.actor.x) < 12 && wy > rt.actor.y - 34 && wy < rt.actor.y + 4) return { kind: 'npc', npc };
    }
    for (const obj of objectsOn(this.map.id, dayId(s))) {
      const r = obj.rect;
      if (wx >= r.x - 4 && wx <= r.x + r.w + 4 && wy >= r.y - (obj.tall ?? 36) && wy <= r.y + r.h + 4) return { kind: 'object', obj };
    }
    return null;
  }

  private distanceTo(t: Target): number {
    return rectDistance(targetRect(t, S(), this), this.player.x, this.player.y);
  }

  private cameraTarget(): { x: number; y: number } {
    const { w, h } = mapSize(this.map);
    const cx = w <= this.viewW ? (w - this.viewW) / 2 : Math.max(0, Math.min(w - this.viewW, this.player.x - this.viewW / 2));
    const cy =
      h + PAD_TOP + PAD_BOTTOM <= this.viewH
        ? (h - this.viewH) / 2
        : Math.max(-PAD_TOP, Math.min(h - this.viewH + PAD_BOTTOM, this.player.y - this.viewH * 0.55));
    return { x: cx, y: cy };
  }

  update(dt: number): void {
    const s = S();
    this.time += dt;
    this.ensureNav();
    if (this.warping) return this.updateWarp(dt);

    s.time += dt * MINUTES_PER_SECOND;
    const gm = dt * MINUTES_PER_SECOND;

    // --- Movement -----------------------------------------------------------
    if (this.ride) this.updateBus(dt);
    const axis = this.hop || this.onBus ? { x: 0, y: 0 } : input.axis();
    let vx = 0, vy = 0;
    if (this.onBus) {
      // Waiting, sitting or stepping off: the bus moves you.
    } else if (this.hop) {
      const h = this.hop;
      h.t = Math.min(1, h.t + dt / HOP_TIME);
      this.player.x = h.fx + (h.tx - h.fx) * h.t;
      this.player.y = h.fy + (h.ty - h.fy) * h.t;
      this.hopZ = Math.sin(h.t * Math.PI) * HOP_HEIGHT;
      if (h.t >= 1) {
        this.hop = null;
        this.hopZ = 0;
        sfx('step');
        this.fx.burst('dust', this.player.x, this.player.y - 1, 4);
      }
    } else if (axis.x || axis.y) {
      this.path = [];
      this.pending = null;
      vx = axis.x;
      vy = axis.y;
    } else if (this.path.length) {
      const wp = this.path[0];
      const dx = wp.x - this.player.x, dy = wp.y - this.player.y;
      const d = Math.hypot(dx, dy);
      if (d < 2.5) this.path.shift();
      else {
        vx = dx / d;
        vy = dy / d;
      }
    }
    const step = SPEED * (s.stats.energy < 20 ? 0.72 : 1) * (riding.on ? RIDE_SPEED[riding.on] : 1) * dt;
    const ox = this.player.x, oy = this.player.y;
    if (vx || vy) {
      let p = this.nav.slide(ox, oy, vx * step, vy * step);
      // On horseback you stay inside the paddock.
      if ((riding.on === 'horse' || riding.on === 'pony') && (this.map.id !== 'town' || !rectContains(PADDOCK, p.x, p.y))) {
        p = { x: ox, y: oy };
        this.cancelMove();
        if (!this.paddockWarned) bus.emit('toast', m().places.horse.leave);
        this.paddockWarned = true;
      } else this.paddockWarned = false;
      this.player.x = p.x;
      this.player.y = p.y;
      if (this.path.length && Math.hypot(p.x - ox, p.y - oy) < step * 0.2) {
        this.stuckFor += dt;
        if (this.stuckFor > 0.4) this.path = [];
      } else this.stuckFor = 0;
    }
    const mdx = this.player.x - ox, mdy = this.player.y - oy;
    if (this.player.animate(dt, mdx, mdy)) {
      if (Math.random() < 0.55) sfx('step');
      this.fx.spawn('dust', this.player.x + (Math.random() - 0.5) * 6, this.player.y - 1);
    }
    s.player.x = this.player.x;
    s.player.y = this.player.y;
    const walked = Math.hypot(mdx, mdy);

    // Doors: step onto one to go through (after leaving the one you arrived on).
    const door = doorAt(this.map, this.player.x, this.player.y);
    if (!door) this.doorLock = false;
    else if (!this.doorLock) {
      if (this.hooks.canUse && !this.hooks.canUse(door)) {
        this.doorLock = true;
        this.cancelMove();
        const back = this.nav.slide(this.player.x, this.player.y, 0, 10);
        this.player.x = back.x;
        this.player.y = back.y;
        return;
      }
      this.warping = { door, t: 0, swapped: false };
      this.cancelMove();
      sfx('door');
      return;
    }

    if (this.pending) {
      const reach = this.pending.kind === 'npc' ? INTERACT_NPC : INTERACT_OBJ;
      if (this.distanceTo(this.pending) < reach) {
        const t = this.pending;
        this.pending = null;
        this.path = [];
        const r = targetRect(t, s, this);
        this.player.face(r.x + r.w / 2, r.y + r.h / 2);
        this.hooks.interact(t);
      } else if (!this.path.length) this.pending = null;
    }

    // --- Needs ----------------------------------------------------------------
    s.stats.energy = clamp100(s.stats.energy - 0.05 * gm - walked * 0.0022);
    s.stats.bladder = clamp100(s.stats.bladder + 0.12 * gm);
    s.stats.mood = clamp100(s.stats.mood - (0.008 + (s.stats.energy < 20 ? 0.03 : 0)) * gm);
    if (this.map.id === 'home') {
      this.familyTimer += dt;
      if (this.familyTimer > 60) {
        this.familyTimer = 0;
        s.stats.mood = clamp100(s.stats.mood + 4);
        s.affection.mom = clamp100(s.affection.mom + 1);
        this.fx.burst('heart', this.player.x, this.player.y - 26, 3);
      }
    } else this.familyTimer = 0;

    this.updateFollowers(dt);
    this.updateNpcs(dt);
    this.npcInit = true;
    for (const [a, b] of this.chatPairs()) {
      const A = this.npcs.get(a)!.actor, B = this.npcs.get(b)!.actor;
      A.face(B.x, B.y);
      B.face(A.x, A.y);
    }
    this.updateNear();
    const night = skyTint(s.time).night;
    this.ambient.update(dt, this.map.id, this.player, night, this.ride && { x: this.ride.x - this.ride.dir * 34, y: this.ride.lane + 2 });
    this.ambientParticles(dt, night);
    this.fx.update(dt);
    if (this.tapMark && (this.tapMark.age += dt) > 0.6) this.tapMark = null;

    const c = this.cameraTarget();
    // Tight follow: a lagging camera makes walking characters look like they float.
    const k = 1 - Math.exp(-dt * 16);
    this.camX += (c.x - this.camX) * k;
    this.camY += (c.y - this.camY) * k;
    this.hooks.tick(dt);
  }

  private updateWarp(dt: number): void {
    const w = this.warping!;
    w.t += dt;
    if (!w.swapped) {
      this.fade = Math.min(1, w.t / FADE);
      if (w.t >= FADE) {
        w.swapped = true;
        const a = w.door.arrive;
        this.teleport(w.door.to, a.x, a.y, a.facing);
        w.t = 0;
      }
    } else {
      this.fade = Math.max(0, 1 - w.t / FADE);
      if (w.t >= FADE) {
        this.fade = 0;
        this.warping = null;
      }
    }
    this.fx.update(dt);
  }

  /** Visual-only update while the world is paused (particles keep drifting). */
  idle(dt: number): void {
    this.time += dt;
    this.fx.update(dt);
    this.npcs.forEach((n) => n.actor.animate(dt, 0, 0));
    this.player.animate(dt, 0, 0);
    if (this.warping) this.updateWarp(dt);
  }

  private updateFollowers(dt: number): void {
    const side = this.player.facing === 'left' ? 1 : -1;
    let tx = this.player.x + side * 15, ty = this.player.y + 3;
    if (this.nav.blocked(tx, ty)) tx = this.player.x - side * 15;
    const k = 1 - Math.exp(-dt * 5);
    this.pet.x += (tx - this.pet.x) * k;
    this.pet.y += (ty - this.pet.y) * k;
    this.pet.hop = this.player.moving ? Math.abs(Math.sin(this.time * 11)) * 4 : Math.max(0, this.pet.hop - dt * 20);
    // The rescued kitten trails behind the pet.
    const kk = 1 - Math.exp(-dt * 4);
    const ox = this.kitten.x, oy = this.kitten.y;
    this.kitten.x += (this.pet.x - side * 12 - this.kitten.x) * kk;
    this.kitten.y += (this.pet.y + 4 - this.kitten.y) * kk;
    this.kitten.actor.x = this.kitten.x;
    this.kitten.actor.y = this.kitten.y;
    this.kitten.actor.animate(dt, this.kitten.x - ox, this.kitten.y - oy);
  }

  private updateNpcs(dt: number): void {
    const s = S();
    // Xiaoyu walks into the counseling room on Friday morning, and later back to class.
    if (dayId(s) === 'fri' && this.map.id === 'school') {
      const xy = s.xiaoyu;
      if (xy.phase === XiaoyuPhase.Absent && s.time >= 8 * 60 + 5 && s.time < 12 * 60) {
        xy.phase = XiaoyuPhase.Walking;
        xy.x = XIAOYU_IN[0].x;
        xy.y = XIAOYU_IN[0].y;
        bus.emit('toast', m().status.xiaoyuComing);
      }
      if (xy.phase === XiaoyuPhase.Walking || xy.phase === XiaoyuPhase.Helped) {
        const route = xy.phase === XiaoyuPhase.Walking ? XIAOYU_IN : XIAOYU_OUT;
        const key = xy.phase === XiaoyuPhase.Walking ? 'xyIn' : 'xyOut';
        const i = Math.min(route.length - 1, s.counters[key] ?? 0);
        const wp = route[i];
        const dx = wp.x - xy.x, dy = wp.y - xy.y, d = Math.hypot(dx, dy);
        const v = Math.min(d, 38 * dt);
        if (d > 1) {
          xy.x += (dx / d) * v;
          xy.y += (dy / d) * v;
        } else if (i < route.length - 1) s.counters[key] = i + 1;
        else if (xy.phase === XiaoyuPhase.Walking) {
          xy.phase = XiaoyuPhase.Seated;
          bus.emit('toast', m().status.xiaoyuSits);
        } else xy.phase = XiaoyuPhase.Gone;
        this.npcs.get('xiaoyu')?.actor.animate(dt, d > 1 ? (dx / d) * v : 0, d > 1 ? (dy / d) * v : 0);
      }
    } else if (dayId(s) === 'fri' && s.xiaoyu.phase === XiaoyuPhase.Absent && s.time >= 8 * 60 + 5) {
      // Off-screen, he just ends up in his seat.
      s.xiaoyu.phase = XiaoyuPhase.Seated;
      s.xiaoyu.x = XIAOYU_IN[XIAOYU_IN.length - 1].x;
      s.xiaoyu.y = XIAOYU_IN[XIAOYU_IN.length - 1].y;
    }

    for (const npc of NPCS) {
      const spot = riding.on !== 'bike' && riding.on === npc.id ? null : npc.where(s);
      let rt = this.npcs.get(npc.id);
      if (!spot || spot.map !== this.map.id) {
        if (!rt) continue;
        // Walk out through the door that leads towards the new place, then vanish.
        if (!rt.leaving) {
          rt.leaving = true;
          const door = this.exitTowards(spot?.map ?? 'town');
          rt.path = door ? this.nav.findPath(rt.actor.x, rt.actor.y, door.x, door.y) ?? [] : [];
        }
        if (!this.walkNpc(rt, dt, 34)) this.npcs.delete(npc.id);
        continue;
      }
      if (!rt || rt.leaving) {
        // Arriving while you're here: come in through the door and walk to the spot.
        const door = this.npcInit && npc.id !== 'xiaoyu' ? this.exitTowards('town') : null;
        const start = door ?? { x: spot.x, y: spot.y };
        rt = { actor: new Actor(start.x, start.y), home: { x: spot.x, y: spot.y }, goal: null, wait: Math.random() * 3, path: [], leaving: false };
        if (door) rt.path = this.nav.findPath(start.x, start.y, spot.x, spot.y) ?? [];
        this.npcs.set(npc.id, rt);
      }
      const a = rt.actor;
      const scripted = npc.id === 'xiaoyu' && (s.xiaoyu.phase === XiaoyuPhase.Walking || s.xiaoyu.phase === XiaoyuPhase.Helped);
      if (scripted) {
        a.x = spot.x;
        a.y = spot.y;
        continue;
      }
      if (rt.home.x !== spot.x || rt.home.y !== spot.y) {
        // The schedule moved them: walk there instead of teleporting.
        rt.home = { x: spot.x, y: spot.y };
        rt.goal = null;
        rt.path = this.nav.findPath(a.x, a.y, spot.x, spot.y) ?? [{ x: spot.x, y: spot.y }];
      }
      if (rt.path.length) {
        this.walkNpc(rt, dt, 34);
        continue;
      }
      const near = Math.hypot(this.player.x - a.x, this.player.y - a.y) < 60;
      if (spot.wander && !near) {
        // Idle wandering around home.
        if (!rt.goal) {
          rt.wait -= dt;
          if (rt.wait <= 0) {
            const ang = Math.random() * Math.PI * 2, r = Math.random() * spot.wander;
            rt.goal = { x: spot.x + Math.cos(ang) * r, y: spot.y + Math.sin(ang) * r * 0.6 };
          }
          a.animate(dt, 0, 0);
        } else {
          const dx = rt.goal.x - a.x, dy = rt.goal.y - a.y, d = Math.hypot(dx, dy);
          const v = Math.min(d, (npc.id === 'cat' ? 26 : 20) * dt);
          if (d < 1) {
            rt.goal = null;
            rt.wait = 1.5 + Math.random() * 4;
            a.animate(dt, 0, 0);
          } else {
            a.x += (dx / d) * v;
            a.y += (dy / d) * v;
            a.animate(dt, (dx / d) * v, (dy / d) * v);
          }
        }
      } else {
        a.animate(dt, 0, 0);
        if (near) a.face(this.player.x, this.player.y);
        else if (!spot.wander) a.facing = 'down';
      }
    }
  }

  /** NPCs standing close to each other turn and chat; returns the chatting pairs. */
  chatPairs(): [NpcId, NpcId][] {
    const out: [NpcId, NpcId][] = [];
    const ids = Array.from(this.npcs.keys()).filter((id) => id !== 'cat');
    for (let i = 0; i < ids.length; i++) {
      for (let j = i + 1; j < ids.length; j++) {
        const a = this.npcs.get(ids[i])!, b = this.npcs.get(ids[j])!;
        if (a.path.length || b.path.length) continue;
        if (Math.hypot(a.actor.x - b.actor.x, a.actor.y - b.actor.y) < 40) out.push([ids[i], ids[j]]);
      }
    }
    return out;
  }

  /** Advance an NPC along its path. Returns false when the path is finished. */
  private walkNpc(rt: NpcRuntime, dt: number, speed: number): boolean {
    const a = rt.actor;
    const wp = rt.path[0];
    if (!wp) {
      a.animate(dt, 0, 0);
      return false;
    }
    const dx = wp.x - a.x, dy = wp.y - a.y, d = Math.hypot(dx, dy);
    if (d < 1.5) {
      rt.path.shift();
      a.animate(dt, 0, 0);
      return rt.path.length > 0;
    }
    const v = Math.min(d, speed * dt);
    a.x += (dx / d) * v;
    a.y += (dy / d) * v;
    a.animate(dt, (dx / d) * v, (dy / d) * v);
    return true;
  }

  /** Centre of the door on this map that leads towards `to`. */
  private exitTowards(to: MapId): { x: number; y: number } | null {
    const next: MapId = this.map.id === 'town' ? to : 'town';
    const door = this.map.doors.find((d) => d.to === next) ?? this.map.doors[0];
    if (!door) return null;
    const [tx, ty] = door.tiles[0];
    // Stand just inside the door, not on the trigger tile.
    const inward = this.map.indoor ? -1 : 1;
    return { x: tx * 16 + (door.tiles.length > 1 ? 16 : 8), y: ty * 16 + 8 + inward * 6 };
  }

  private updateNear(): void {
    const s = S();
    let best: Target | null = null;
    let bestD = Infinity;
    for (const npc of NPCS) {
      if (!this.npcs.has(npc.id)) continue;
      const t: Target = { kind: 'npc', npc };
      const d = this.distanceTo(t);
      if (d < INTERACT_NPC && d < bestD) {
        best = t;
        bestD = d;
      }
    }
    for (const obj of objectsOn(this.map.id, dayId(s))) {
      const d = rectDistance(obj.rect, this.player.x, this.player.y);
      if (d < INTERACT_OBJ && d + 4 < bestD) {
        best = { kind: 'object', obj };
        bestD = d;
      }
    }
    // Standing on a treasure spot beats everything else: time to dig.
    const spot = spotHere(this.map.id, this.player.x, this.player.y);
    if (spot) best = { kind: 'spot', spot };
    if (this.onBus) best = null;
    const changed = targetKey(best) !== targetKey(this.near);
    this.near = best;
    if (changed) bus.emit('change');
  }

  npcPosition(id: NpcId): { x: number; y: number } | null {
    const rt = this.npcs.get(id);
    return rt ? { x: rt.actor.x, y: rt.actor.y } : null;
  }

  private ambientParticles(dt: number, night: number): void {
    const s = S();
    const vx0 = this.camX - 30, vx1 = this.camX + this.viewW + 30;
    const vy0 = this.camY - 40, vy1 = this.camY + this.viewH + 40;
    const vis = (x: number, y: number) => x > vx0 && x < vx1 && y > vy0 && y < vy1;
    const e = this.emit;
    const every = (key: keyof typeof e, rate: number, fn: () => void) => {
      e[key] += dt * rate;
      while (e[key] >= 1) {
        e[key] -= 1;
        fn();
      }
    };
    if (this.map.id === 'school') {
      if (vis(40, 100)) every('steam', 3, () => this.fx.spawn('steam', 26 + (Math.random() - 0.5) * 4, 92));
      if (s.time < 12 * 60 && vis(120, 60)) every('motes', 4, () => this.fx.spawn('mote', 70 + Math.random() * 90, 30 + Math.random() * 120));
    }
    if (this.map.id === 'home' && vis(170, 30)) every('steam', 1.5, () => this.fx.spawn('steam', 170 + (Math.random() - 0.5) * 6, 26));
    if (this.map.id === 'beitou') {
      // Wisps rising off the hot stream and the thermal pools (every steam prop that is in view).
      const vents = this.map.decor.filter((d) => d.kind === 'ck_steam' && vis(d.x, d.y));
      if (vents.length) every('steam', 2 + vents.length * 1.5, () => {
        const d = vents[Math.floor(Math.random() * vents.length)];
        this.fx.spawn('steam', d.x + (Math.random() - 0.5) * 20, d.y - 4 - Math.random() * 6);
      });
    }
    if (!this.map.indoor) {
      // Petals blow off the cherry trees (more in gusts); leaves only in strong gusts.
      const gust = wind.strength();
      for (const d of this.map.decor) {
        if (d.kind !== 'cherry' && d.kind !== 'tree') continue;
        if (!vis(d.x + 40, d.y)) continue;
        const rate = d.kind === 'cherry' ? 0.5 + gust * 2.2 : Math.max(0, gust - 0.85) * 1.5;
        if (Math.random() < rate * dt) this.fx.spawn(d.kind === 'cherry' ? 'petal' : 'leaf', d.x + (Math.random() - 0.5) * 34, d.y - 34 - Math.random() * 18);
      }
      void every;
      if (night > 0.4) every('flies', 2 * night, () => this.fx.spawn('firefly', this.camX + Math.random() * this.viewW, this.camY + Math.random() * this.viewH));
    }
    if (s.stats.mood >= 80 && this.player.moving) every('notes', 0.7, () => this.fx.spawn('note', this.player.x + 6, this.player.y - 34));
  }
}

export function targetRect(t: Target, s = S(), world?: World): Rect {
  if (t.kind === 'object') return t.obj.rect;
  if (t.kind === 'spot') return { x: t.spot.x - 6, y: t.spot.y - 4, w: 12, h: 6 };
  const live = world?.npcPosition(t.npc.id);
  const p = live ?? t.npc.where(s) ?? { x: -999, y: -999 };
  return { x: p.x - 7, y: p.y - 6, w: 14, h: 8 };
}

export function targetKey(t: Target | null): string {
  if (!t) return '';
  return t.kind === 'npc' ? `npc:${t.npc.id}` : t.kind === 'spot' ? `spot:${t.spot.id}` : `obj:${t.obj.id}`;
}
